import { useState, useMemo, useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import PortalIdParser from 'PortalIdParser';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Text } from 'foundations-components/components/Text';
import H2 from 'UIComponents/elements/headings/H2';
import UIGrid from 'UIComponents/grid/UIGrid';
import UIGridItem from 'UIComponents/grid/UIGridItem';
import UIFlex from 'UIComponents/layout/UIFlex';
import LoadingWrapper from 'marketplace-ui-common/layout/components/LoadingWrapper';
import ReviewsListStats from 'marketplace-ui-reviews/components/ReviewsListStats';
import ReviewsHeader from 'marketplace-ui-reviews/components/ReviewsHeader';
import ReviewsFilterSortDropdowns from 'marketplace-ui-reviews/components/ReviewsFilterSortDropdowns';
import ReviewsPaginatedList from 'marketplace-ui-reviews/components/ReviewsPaginatedList';
import { RELEVANCE, REVIEWS_PER_PAGE, SEARCH_KEY } from 'marketplace-ui-reviews/constants/ReviewsListQueries';
import { useReviewability } from 'marketplace-ui-reviews/hooks/useReviewability';
import * as ReviewTypes from 'marketplace-ui-reviews/constants/ReviewType';
import ReviewFilters from 'marketplace-ui-reviews/records/ReviewFilters';
import { useReviews } from 'marketplace-ui-reviews/hooks/useReviews';
import { usePrevious } from 'marketplace-ui-common/hooks/usePrevious';
import { MediaQueryView } from 'marketplace-ui-common/layout/components/MediaQueryView';
import DetailsReviewRenderer from './DetailsReviewRenderer';
import WriteReviewButton from './WriteReviewButton';
// @ts-expect-error untyped dependency
import { getIsPublic } from '../../../selectors/auth';
import ReviewsListEmptyState from './ReviewsListEmptyState';
import { AppReviewWizard } from './AppReviewWizard';
import { replace } from '../../../utils/history';
import { REVIEWS_LIST_WIDTH, MINIMUM_REVIEWS_NEEDED_TO_DISPLAY } from '../../../constants/reviews';
import { useReviewFilters } from 'marketplace-ui-reviews/hooks/useReviewFilters';
import { useAppContext } from '../../../context/AppContext';
import { View } from 'foundations-components/components/View';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export default function Reviews({
  allReviewsHref,
  appName,
  appIcon,
  isPreview,
  isReviewLink,
  listingDetailUrl,
  offeringId,
  offeringPortalId,
  slug,
  supportLink,
  trackInteraction
}) {
  const isPublic = useSelector(getIsPublic);
  const [submitted, setSubmitted] = useState(false);
  const {
    reviewFilters,
    updateReviewFilters
  } = useReviewFilters(new ReviewFilters({
    reviewTypes: [ReviewTypes.APP],
    entityId: offeringId,
    sort: RELEVANCE
  }));
  const appContext = useAppContext();
  const {
    reviews,
    reviewStats,
    reviewsFetchStatus,
    reviewStatsFetchStatus,
    reviewsTotal,
    reviewsCount
  } = useReviews({
    appContext,
    reviewFilters,
    offeringId,
    reviewType: 'APP'
  });
  const {
    reviews: userReviews
  } = useReviews({
    appContext,
    reviewFilters: new ReviewFilters({
      reviewTypes: [ReviewTypes.APP],
      entityId: isPublic ? undefined : offeringId,
      reviewerPortalId: PortalIdParser.get()
    }),
    offeringId,
    reviewType: 'APP'
  });
  const userReview = userReviews[0];
  const {
    unreviewableReason,
    requestState: reviewabilityRequestStatus
  } = useReviewability({
    editPending: !!userReview && submitted,
    entityId: offeringId,
    entityPortalId: offeringPortalId,
    isPublicApp: isPublic,
    reviewPending: !userReview && submitted,
    reviewType: 'APP'
  });
  const [wizardOpen, setWizardOpen] = useState(isReviewLink);
  const [searchTerm, setSearchTerm] = useState('');
  const renderedWriteReviewButton = useMemo(() => {
    return /*#__PURE__*/_jsx(UIFlex, {
      justify: "end",
      children: /*#__PURE__*/_jsx(WriteReviewButton, {
        disabled: isPreview,
        hasEditableReview: !isPublic && !!userReview,
        onClick: () => setWizardOpen(true),
        unreviewableReason: unreviewableReason,
        marginBlockEnd: "400"
      })
    });
  }, [isPreview, isPublic, unreviewableReason, userReview]);
  const onUpdateFilters = useCallback(({
    textKey,
    value
  }) => {
    if (textKey === SEARCH_KEY) {
      setSearchTerm(String(value));
    }
    updateReviewFilters({
      [textKey]: value
    });
    trackInteraction(`change reviews ${textKey}`, {
      section: 'review',
      value
    });
  }, [updateReviewFilters, trackInteraction]);
  const prevOfferingId = usePrevious(offeringId);
  useEffect(() => {
    // Update the reviewFilters when the offeringId changes
    if (offeringId !== prevOfferingId) {
      updateReviewFilters({
        entityId: offeringId
      });
    }
  }, [offeringId, prevOfferingId, updateReviewFilters]);
  const renderedReviewsList = useMemo(() => /*#__PURE__*/_jsxs("div", {
    "data-test-id": "reviewsList",
    style: {
      maxWidth: REVIEWS_LIST_WIDTH
    },
    children: [/*#__PURE__*/_jsx(ReviewsHeader, {
      onUpdateFilters: onUpdateFilters,
      renderedButtonRow: renderedWriteReviewButton,
      reviewsCount: reviewsTotal,
      reviewFilters: reviewFilters
    }), /*#__PURE__*/_jsx(ReviewsFilterSortDropdowns, {
      onUpdateFilters: onUpdateFilters,
      reviewFilters: reviewFilters,
      reviewStats: reviewStats
    }), /*#__PURE__*/_jsx(ReviewsPaginatedList, {
      onUpdateFilters: onUpdateFilters,
      ReviewRenderer: DetailsReviewRenderer,
      reviews: reviews,
      reviewsFetchStatus: reviewsFetchStatus,
      reviewFilters: reviewFilters,
      reviewsTotal: reviewsTotal,
      showPagination: reviewsTotal >= REVIEWS_PER_PAGE,
      searchTerm: searchTerm,
      onTrackInteraction: trackInteraction
    })]
  }), [onUpdateFilters, renderedWriteReviewButton, reviewsTotal, reviewFilters, reviewStats, reviews, reviewsFetchStatus, searchTerm, trackInteraction]);
  const renderedReviewsBody = useMemo(() => {
    if (reviewsCount >= MINIMUM_REVIEWS_NEEDED_TO_DISPLAY) {
      return /*#__PURE__*/_jsxs(UIGrid, {
        className: "m-top-8",
        children: [/*#__PURE__*/_jsxs(UIGridItem, {
          size: {
            xs: 12,
            md: 5,
            lg: 4
          },
          children: [/*#__PURE__*/_jsx(LoadingWrapper, {
            requestStatus: reviewStatsFetchStatus,
            children: /*#__PURE__*/_jsx(ReviewsListStats, {
              allReviewsHref: allReviewsHref,
              onUpdateFilters: onUpdateFilters,
              reviewStats: reviewStats,
              reviewType: ReviewTypes.APP
            })
          }), /*#__PURE__*/_jsx(MediaQueryView, {
            min: "md",
            children: /*#__PURE__*/_jsxs(UIFlex, {
              direction: "column",
              className: "p-top-5",
              children: [/*#__PURE__*/_jsx(Text, {
                as: "h3",
                marginBlockEnd: "100",
                typography: "heading-100",
                children: /*#__PURE__*/_jsx(FormattedMessage, {
                  message: "appMarketplace.detail.reviews.write.ctaTitle"
                })
              }), /*#__PURE__*/_jsx(FormattedMessage, {
                message: "appMarketplace.detail.reviews.write.ctaDescription"
              }), /*#__PURE__*/_jsx(View, {
                marginBlockStart: "400",
                as: "div",
                children: /*#__PURE__*/_jsx(WriteReviewButton, {
                  disabled: isPreview,
                  onClick: () => setWizardOpen(true),
                  hasEditableReview: !isPublic && !!userReview,
                  tooltipPlacement: "right bottom",
                  unreviewableReason: unreviewableReason
                })
              })]
            })
          }), /*#__PURE__*/_jsx(MediaQueryView, {
            max: "md",
            children: /*#__PURE__*/_jsx("div", {
              style: {
                height: 24
              }
            })
          })]
        }), /*#__PURE__*/_jsx(UIGridItem, {
          size: {
            xs: 12,
            md: 7,
            lg: 8
          },
          children: renderedReviewsList
        })]
      });
    } else if (reviewsCount && reviewsCount < MINIMUM_REVIEWS_NEEDED_TO_DISPLAY) {
      return renderedReviewsList;
    } else if (!reviewsCount) {
      return /*#__PURE__*/_jsx(ReviewsListEmptyState, {
        isPreview: isPreview,
        onWriteReviewClick: () => setWizardOpen(true),
        unreviewableReason: unreviewableReason
      });
    }
    return null;
  }, [isPublic, reviewsCount, reviewStatsFetchStatus, allReviewsHref, onUpdateFilters, reviewStats, isPreview, unreviewableReason, userReview, renderedReviewsList]);
  const renderedWizard = useMemo(() => /*#__PURE__*/_jsx(AppReviewWizard, {
    appName: appName,
    appIconUrl: appIcon,
    editedReview: userReview,
    offeringId: offeringId,
    onClose: () => setWizardOpen(false),
    onSuccess: () => {
      setWizardOpen(false);
      replace(listingDetailUrl);
      setSubmitted(true);
    },
    onTrackInteraction: trackInteraction,
    slug: slug,
    supportLink: supportLink || undefined,
    unreviewableReason: unreviewableReason
  }), [appIcon, appName, offeringId, listingDetailUrl, trackInteraction, slug, supportLink, unreviewableReason, userReview]);
  return /*#__PURE__*/_jsx(LoadingWrapper, {
    requestStatus: reviewabilityRequestStatus,
    children: /*#__PURE__*/_jsxs("div", {
      style: {
        position: 'relative'
      },
      children: [/*#__PURE__*/_jsx(H2, {
        className: "m-bottom-8",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "appMarketplace.detail.reviews.headingText",
          options: {
            name: appName
          }
        })
      }), renderedReviewsBody, wizardOpen && renderedWizard]
    })
  });
}